import React, { useRef } from "react";
import PropTypes from "prop-types";
import { useSharedTranslation } from "@/shared/contexts/TranslateContext";
import classNames from "classnames";

import Ripple from "@/shared/components/Button/Ripple";

import { useTooltipContext } from '@/shared/components';
import { useLongPress } from '@/shared/components/LongPressWrapper'
import { useWhatInputIsLastPointerMouse } from '@/shared/what-input/useWhatInput'

import { TAB_SIZE, TAB_VARIANT, TAB_COLOR } from "./Tab.constants";

import styles from "./Tab.module.scss";

const DelayDoubleClick = 300;

const Tab = ({
  className,
  classNameActive,

  number,

  isLocked,
  isSelected,
  isActive,
  variant,
  size,
  color,

  onSelect,
  onLock,

  children,
}) => {
  const { t } = useSharedTranslation();

  const { showTooltip } = useTooltipContext();
  const isLastPointerMouse = useWhatInputIsLastPointerMouse();

  const refTimestampLastClick = useRef(0);
  const refIdTimerDoubleClick = useRef(undefined);

  const checkDoubleClick = () => {
    if (refTimestampLastClick.current === 0) {
      refTimestampLastClick.current = Date.now();
    }

    const currentClickTime = Date.now();

    const lastClickTime = refTimestampLastClick.current;

    refTimestampLastClick.current = currentClickTime;

    const timeOffset = currentClickTime - lastClickTime;

    return timeOffset > 0 && timeOffset <= DelayDoubleClick;
  };

  const handleClick = (e) => {
    if (onLock) {
      if (!refIdTimerDoubleClick.current) {
        refIdTimerDoubleClick.current = setTimeout(() => {
          onSelect(e);

          refIdTimerDoubleClick.current = undefined;
        }, DelayDoubleClick);
      }

      if (checkDoubleClick()) {
        clearTimeout(refIdTimerDoubleClick.current);
        refIdTimerDoubleClick.current = undefined;
        onLock(e);
      }
    } else {
      onSelect(e);
    }
  };

  const showLockTooltip = (e) => {
    const hint = isLocked ? "unlockColumnHint" : "lockColumnHint";
    showTooltip(t(hint), e);
  }

  const longPressListeners = useLongPress({
    onLongPress: showLockTooltip,
    onClick: handleClick,
    onContextMenu: showLockTooltip,
  });

  const ariaLabel = typeof children === "string" ? children : "";

  const isActiveFinal = isSelected || isActive;

  return (
    <span
      tabIndex={-1}
      role="tab"
      aria-label={ariaLabel ? t("tabLabel", { label: ariaLabel }) : undefined}
      aria-selected={isSelected ? "true" : "false"}
      className={classNames(styles["tab"], "tab-default", className,
        styles[size], styles[variant], styles[color], {
          [classNameActive]: isActiveFinal,
          [styles["active"]]: isActiveFinal,
          [styles["locked"]]: isLocked,
        })}
      onClick={handleClick}
      {...longPressListeners}
      onMouseEnter={(isLastPointerMouse && onLock) ? showLockTooltip : undefined}
    >
      {children}
      <Ripple />
    </span>
  )
};

Tab.variant = TAB_VARIANT;
Tab.size = TAB_SIZE;
Tab.color = TAB_COLOR;

Tab.defaultProps = {
  variant: TAB_VARIANT.underlined,
  color: TAB_COLOR.primary,
};

Tab.propTypes = {
  variant: PropTypes.oneOf(Object.values(TAB_VARIANT)),
  size: PropTypes.oneOf(Object.values(TAB_SIZE)),
  color: PropTypes.oneOf(Object.values(TAB_COLOR)),
};

export default Tab;
